// Swappable font-group presets. Each group is named after the theme its font
// combination originally came from, but can be applied to any theme.
//
// button are additive keys introduced for the
// "combo" group. Existing groups map them onto their current body font with
// no forced weight, so referencing these keys never changes the four
// original presets' appearance.
//
// displayTitle / reviewerName / pairValue / pairCaption / statCaption /
// verified / priceValue / itemDescription / favFactValue / favFactCaption
// are additional roles covering elements whose original per-theme markup
// used a DIFFERENT generic role name (label/body/name/socialStat) for the
// *same kind* of element in each theme. Every value below is that theme's
// own pre-existing literal for that exact element, so referencing these
// roles does not change any theme's own default rendering — it only makes
// cross-theme font-group application consistent (e.g. applying "Edge" to
// another theme's "Verified" badge now resolves to Edge's own Verified-badge
// font, not whatever unrelated role that theme's file happened to reuse).
export const FONT_GROUPS = {
  standard: {
    heading: 'font-outfit',
    name: 'font-noto',
    profileName: 'font-noto font-normal uppercase text-[40px] sm:text-[64px] base:text-[56] xxl:text-[64px] leading-[102%]',
    profileUserName: 'font-noto font-normal text-xl',
    profilePairValue: 'font-noto font-normal text-xl md:text-2xl xxl:text-[32px]',
    profilePairCaption: 'font-outfit font-light uppercase tracking-[0.18em] text-sm md:text-base',
    profileSocialStat: 'font-notodisplay font-medium uppercase text-[40px] leading-[102%]',
    profileStatCaption: 'font-outfit font-light text-base leading-[160%]',
    socialStat: 'font-notodisplay',
    label: 'font-noto',
    body: 'font-outfit',
    button: 'font-outfit',
    verified: 'font-outfit',
    favFactValue: 'font-notodisplay font-normal text-5xl sm:text-4xl lg:text-6xl leading-[100%]',
    favFactCaption: 'font-outfit font-light uppercase tracking-[3px] text-sm',
    topInterestHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    topInterestItem: 'font-notodisplay font-normal text-[40px] smd:text-[45px] base:text-[50px] xl:text-[56px] !leading-[130%]',
    brandPartnersHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    brandPartnerName: 'font-notodisplay font-normal text-3xl',
    deliverablesHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    deliverableTitle: 'font-notodisplay font-medium text-[32px] md:text-4xl lg:text-[40px] xl:text-5xl leading-[102%]',
    deliverableDescription: 'font-noto font-normal text-sm base:text-base leading-[150%]',
    ctaBannerTitle: 'font-notodisplay font-medium text-xl md:text-4xl lg:text-5xl',
    ctaBannerButton: 'font-outfit font-medium uppercase tracking-widest text-base',
    audienceHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    audienceSubHeading: 'font-outfit font-light text-lg base:text-2xl',
    audienceItemLabel: 'font-outfit font-normal text-base',
    audienceItemValue: 'font-noto font-semibold text-base',
    ratesHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    rateChannelTitle: 'font-notodisplay font-medium text-2xl smd:text-[28px] lg:text-[32px] xl:text-[40px] leading-[130%]',
    ratePriceValue: 'font-noto font-light text-2xl smd:text-[32px]',
    topPostStatValue: 'font-notodisplay text-xl smd:text-[20px] base:text-[22px] lg:text-[26px] xl:text-[30px] leading-[1.5rem] base:leading-[2.5rem]',
    topPostStatCaption: 'font-noto font-light text-xs',
    topPostCaptionTitle: 'font-outfit font-medium text-base sm:text-xl base:text-2xl leading-[1.2]',
    topPostCaptionSubtitle: 'font-baskervville italic font-normal text-sm sm:text-lg text-neutral',
    reviewCreatorName: 'font-notodisplay font-light text-[40px] lg:text-6xl leading-[125%]',
    reviewOverallRating: 'font-notodisplay font-medium text-[40px] sm:text-5xl lg:text-6xl leading-[102%]',
    reviewOverallCaption: 'font-outfit font-light uppercase tracking-[3px] text-sm md:text-lg',
    reviewCriteriaLabel: 'font-outfit font-light uppercase tracking-[3px] text-base',
    reviewCardTitle: 'font-outfit uppercase tracking-widest text-xl',
    reviewCardBody: 'font-outfit font-light text-base leading-[130%]',
    topVideosBanner: 'font-outfit font-normal text-base',
    reviewFeedbackSubline: 'font-outfit font-light text-base',
    linksHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    linkItemTitle: 'font-notodisplay font-medium text-2xl md:text-[34px] xl:text-[40px] leading-[130%]',
    socialsHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    socialHandle: 'font-outfit font-light text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    mailHeading: 'font-outfit font-light uppercase tracking-[0.18em]',
    mailAddress: 'font-outfit font-light text-lg md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    verificationBannerText: 'font-outfit font-extralight text-lg lg:text-xl',
    bio: 'Outfit, sans-serif'
  },
  dark: {
    heading: 'font-roboto',
    name: 'font-antonio',
    profileName: 'font-antonio font-bold uppercase tracking-[0.025em] text-[40px] sm:text-[64px] base:text-[56] xxl:text-[64px] leading-[102%]',
    profileUserName: 'font-antonio font-normal text-xl',
    profilePairValue: 'font-roboto font-normal tracking-[0.08em] text-lg md:text-2xl xl:text-[28px] leading-[114%]',
    profilePairCaption: 'font-roboto font-light uppercase tracking-[3px] text-sm',
    profileSocialStat: 'font-antonio font-medium text-[40px] leading-[102%]',
    profileStatCaption: 'font-outfit font-light text-base leading-[160%]',
    socialStat: 'font-antonio',
    label: 'font-outfit',
    body: 'font-roboto',
    button: 'font-roboto',
    verified: 'font-outfit',
    favFactValue: 'font-antonio font-semibold text-[32px] uppercase md:text-[44px] lg:text-[56px] leading-[100%]',
    favFactCaption: 'font-outfit font-light uppercase tracking-[3px] text-sm',
    topInterestHeading: 'font-roboto uppercase',
    topInterestItem: 'font-antonio font-semibold tracking-[0.025em] text-[28px] md:text-[40px] lg:text-[56px] leading-[130%]',
    brandPartnersHeading: 'font-roboto uppercase',
    brandPartnerName: 'font-antonio font-bold uppercase text-3xl tracking-[0.025em]',
    deliverablesHeading: 'font-roboto uppercase',
    deliverableTitle: 'font-antonio font-medium text-[32px] sm:text-[40px] base:text-5xl leading-[102%]',
    deliverableDescription: 'font-roboto font-normal text-base sm:text-lg base:text-xl leading-[150%]',
    ctaBannerTitle: 'font-antonio font-semibold uppercase text-base xs:text-2xl base:text-4xl xl:text-[56px]',
    ctaBannerButton: 'font-roboto max-md:font-antonio font-bold uppercase tracking-[0.08em] md:text-base text-[32px]',
    audienceHeading: 'font-roboto font-medium uppercase tracking-[0.08em]',
    audienceSubHeading: 'font-roboto font-medium uppercase tracking-[0.08em] text-lg base:text-2xl',
    audienceItemLabel: 'font-roboto font-normal text-base',
    audienceItemValue: 'font-roboto font-semibold text-base',
    ratesHeading: 'font-roboto font-light uppercase tracking-[0.08em]',
    rateChannelTitle: 'font-antonio font-light tracking-[0.025em] text-2xl md:text-[32px] lg:text-[40px] leading-[125%]',
    ratePriceValue: 'font-antonio font-thin tracking-[0.025em] text-2xl smd:text-[32px] lg:text-[40px]',
    topPostStatValue: 'font-antonio uppercase text-xl smd:text-[22px] base:text-2xl lg:text-[28px] xl:text-[32px] leading-[1.2]',
    topPostStatCaption: 'font-roboto text-[12px] md:text-xs md:text-xl',
    topPostCaptionTitle: 'font-antonio uppercase font-bold text-xl lg:text-2xl leading-[1.2]',
    topPostCaptionSubtitle: 'font-baskervville italic font-normal text-sm sm:text-lg text-neutral',
    reviewCreatorName: 'font-antonio font-normal text-[40px] lg:text-[56px] leading-[125%]',
    reviewOverallRating: 'font-antonio font-semibold text-[40px] sm:text-5xl lg:text-6xl leading-[102%]',
    reviewOverallCaption: 'font-roboto font-light uppercase tracking-[3px] text-sm md:text-lg',
    reviewCriteriaLabel: 'font-roboto uppercase tracking-[3px] text-base font-light',
    reviewCardTitle: 'font-antonio font-normal max-md:font-bold uppercase tracking-widest text-[20px]',
    reviewCardBody: 'font-roboto md:font-light text-base leading-[130%]',
    topVideosBanner: 'font-roboto font-normal text-base',
    reviewFeedbackSubline: 'font-roboto font-light text-base',
    linksHeading: 'font-roboto font-light uppercase tracking-[0.08em]',
    linkItemTitle: 'font-antonio max-md:uppercase font-semibold tracking-[0.025em] text-lg smd:text-[28px] base:text-[34px] xl:text-[40px] leading-[125%]',
    socialsHeading: 'font-roboto font-light uppercase tracking-[0.08em]',
    socialHandle: 'font-roboto font-light text-base md:text-xl lg:text-2xl leading-[130%]',
    mailHeading: 'font-roboto font-light uppercase tracking-[0.08em]',
    mailAddress: 'font-roboto font-light text-lg md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    verificationBannerText: 'font-roboto font-light text-lg lg:text-xl',
    bio: 'Roboto, sans-serif'
  },
  natural: {
    heading: 'font-nunitosans',
    name: 'font-mollie',
    profileName: 'font-nunito font-normal tracking-[-0.045em] text-[40px] md:text-[52px] base:text-[62px] lg:text-7xl xl:text-[80px] leading-[102%]',
    profileUserName: 'font-nunito font-normal text-xl',
    profilePairValue: 'font-nunito font-medium text-lg xxl:text-[28px] leading-[150%]',
    profilePairCaption: 'font-nunito font-light uppercase tracking-[3px] text-sm leading-[100%]',
    profileSocialStat: 'font-nunitosans font-medium text-[32px] md:text-[40px] leading-[102%]',
    profileStatCaption: 'font-nunitosans font-light text-base',
    socialStat: 'font-nunitosans',
    label: 'font-nunitosans',
    body: 'font-nunito',
    button: 'font-nunito',
    verified: 'font-nunito',
    favFactValue: 'font-nunito font-semibold text-[40px] md:text-4xl uppercase leading-[100%]',
    favFactCaption: 'font-nunitosans font-semibold uppercase tracking-[3px] text-sm',
    topInterestHeading: 'font-nunito font-medium',
    topInterestItem: 'font-nunito font-medium text-[40px] leading-[120%]',
    brandPartnersHeading: 'font-nunito font-medium',
    brandPartnerName: 'font-nunito font-medium text-3xl',
    deliverablesHeading: 'font-nunito font-medium',
    deliverableTitle: 'font-nunito font-medium text-[32px] md:text-[36px] base:text-[40px] leading-[102%]',
    deliverableDescription: 'font-nunitosans font-normal text-sm md:text-base leading-[150%]',
    ctaBannerTitle: 'font-nunito font-bold text-sm md:text-2xl lg:text-[32px]',
    ctaBannerButton: 'font-nunito font-bold uppercase tracking-[0.25rem]',
    ctaBannerButtonMobile: 'font-nunito font-light',
    audienceHeading: 'font-nunito font-medium',
    audienceSubHeading: 'font-nunito font-medium text-lg base:text-2xl',
    audienceItemLabel: 'font-nunitosans font-normal text-base',
    audienceItemValue: 'font-nunitosans font-semibold text-base',
    ratesHeading: 'font-nunito font-medium',
    rateChannelTitle: 'font-nunito font-medium text-2xl smd:text-[28px] lg:text-[32px] leading-[130%]',
    ratePriceValue: 'font-nunitosans font-medium text-2xl smd:text-[32px]',
    topPostStatValue: 'font-nunitosans font-medium text-xl smd:text-[22px] base:text-2xl lg:text-[28px] xl:text-[32px]',
    topPostStatCaption: 'font-nunitosans font-light text-sm',
    topPostCaptionTitle: 'font-nunito font-medium text-base sm:text-2xl',
    topPostCaptionSubtitle: 'font-baskervville font-normal italic text-sm sm:text-lg text-neutral',
    reviewCreatorName: 'font-nunito text-[40px] md:text-5xl base:text-[56px] lg:text-[64px] font-normal leading-[102%]',
    reviewOverallRating: 'font-nunito font-medium text-[40px] sm:text-5xl lg:text-6xl leading-[102%]',
    reviewOverallCaption: 'font-nunitosans font-light uppercase tracking-[3px] text-sm md:text-lg',
    reviewCriteriaLabel: 'font-nunitosans font-light uppercase tracking-[3px] text-base',
    reviewCardTitle: 'font-nunito font-medium tracking-widest text-2xl',
    reviewCardBody: 'font-nunitosans font-normal text-base leading-[130%]',
    topVideosBanner: 'font-nunitosans font-normal text-base',
    reviewFeedbackSubline: 'font-baskervville font-normal text-base md:text-lg',
    linksHeading: 'font-nunito font-medium',
    linkItemTitle: 'font-nunito font-medium text-2xl md:text-[34px] xl:text-[40px] leading-[130%]',
    socialsHeading: 'font-nunito font-medium',
    socialHandle: 'font-nunitosans font-normal text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    mailHeading: 'font-nunito font-medium',
    mailAddress: 'font-nunitosans font-normal text-lg md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    verificationBannerText: 'font-nunitosans font-normal text-lg lg:text-xl',
    bio: 'Nunito Sans, sans-serif'
  },
  classic: {
    heading: 'font-roboto',
    name: 'font-roboto',
    profileName: 'font-roboto font-bold text-[40px] sm:text-5xl base:text-[64px] xl:text-7xl leading-[110%]',
    profilePairValue: 'font-roboto font-medium text-lg md:text-2xl xxl:text-[32px] leading-[150%]',
    profilePairCaption: 'font-roboto font-light uppercase tracking-[0.2em] text-sm md:text-base',
    profileSocialStat: 'font-notodisplay font-medium text-[40px] leading-[102%]',
    profileStatCaption: 'font-outfit font-light text-base leading-[160%]',
    socialStat: 'font-notodisplay',
    label: 'font-outfit',
    body: 'font-roboto',
    button: 'font-roboto',
    verified: 'font-outfit',
    favFactValue: 'font-roboto font-medium text-5xl md:text-[52px] lg:text-[56px] uppercase leading-[100%]',
    favFactCaption: 'font-outfit font-light uppercase tracking-[3px] text-sm',
    topInterestHeading: 'font-outfit font-medium uppercase',
    topInterestItem: 'font-roboto font-light uppercase text-lg smd:text-xl base:text-2xl tracking-[3px]',
    brandPartnersHeading: 'font-outfit font-medium uppercase',
    brandPartnerName: 'font-notodisplay font-normal uppercase text-3xl tracking-[1px]',
    deliverablesHeading: 'font-outfit font-medium uppercase',
    deliverableTitle: 'font-roboto font-medium text-[32px] md:text-[40px] lg:text-5xl leading-[102%]',
    deliverableDescription: 'font-roboto font-normal text-base md:text-xl leading-[160%]',
    ctaBannerTitle: 'font-roboto font-medium text-xl md:text-4xl lg:text-5xl',
    ctaBannerButton: 'font-outfit max-md:font-roboto font-normal max-md:font-medium uppercase tracking-widest text-base max-md:text-[32px]',
    audienceHeading: 'font-outfit font-medium uppercase tracking-[0.2em]',
    audienceSubHeading: 'font-roboto font-normal text-lg base:text-2xl',
    audienceItemLabel: 'font-roboto font-normal text-base',
    audienceItemValue: 'font-roboto font-semibold text-base',
    ratesHeading: 'font-outfit font-medium uppercase',
    rateChannelTitle: 'font-roboto font-light max-md:uppercase md:!font-medium text-2xl smd:text-[28px] lg:text-[32px] xl:text-[40px] leading-[130%]',
    ratePriceValue: 'font-roboto font-light text-2xl smd:text-[40px]',
    topPostHeading: 'font-outfit',
    topPostStatValue: 'font-roboto font-normal text-xl smd:text-[22px] base:text-2xl lg:text-[28px] xl:text-[32px]',
    topPostStatCaption: 'font-outfit font-light text-xs md:text-sm',
    topPostCaptionTitle: 'font-roboto font-medium text-[22px] md:text-2xl',
    topPostCaptionSubtitle: 'font-baskervville font-normal italic text-sm sm:text-lg text-neutral',
    reviewCreatorName: 'font-roboto font-normal text-4xl md:text-[56px] leading-[125%]',
    reviewOverallRating: 'font-notodisplay font-medium text-[40px] sm:text-5xl lg:text-6xl leading-[102%]',
    reviewOverallCaption: 'font-roboto font-light uppercase tracking-[3px] text-sm md:text-lg',
    reviewCriteriaLabel: 'font-outfit font-light uppercase tracking-[3px] text-base',
    reviewCardTitle: 'font-notodisplay font-normal max-md:font-bold tracking-widest text-xl',
    reviewCardBody: 'font-outfit font-light text-base leading-[130%]',
    topVideosBanner: 'font-roboto font-normal text-base',
    reviewFeedbackSubline: 'font-roboto max-md:font-baskervville font-light text-base',
    linksHeading: 'font-outfit font-medium uppercase',
    linkItemTitle: 'font-roboto font-light md:font-medium max-md:uppercase text-lg md:text-3xl lg:text-[40px] leading-[130%]',
    socialsHeading: 'font-outfit font-medium uppercase',
    socialHandle: 'font-outfit font-light text-base md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    mailHeading: 'font-outfit font-medium uppercase',
    mailAddress: 'font-outfit font-light text-base md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    verificationBannerText: 'font-roboto font-light text-lg lg:text-xl',
    bio: 'Outfit, sans-serif'
  },
  // 5th combination: Syne / Work Sans / Fraunces Italic / Space Mono.
  combo: {
    heading: 'font-syne !font-semibold',           // section titles — Syne 600
    name: 'font-syne !font-bold',                  // creator's name — Syne 700
    profileName: 'font-syne !font-medium uppercase tracking-[-0.055em] text-[40px] sm:text-[64px] base:text-[56] xxl:text-[64px] leading-[102%]',            // top profile username — Syne 700
    profileUserName: 'font-syne !font-bold text-xl',
    profilePairValue: 'font-worksans !font-medium text-xl md:text-2xl xxl:text-[32px]', // age/location values — Work Sans 500
    profilePairCaption: 'font-spacemono !font-normal uppercase tracking-[0.1em] text-sm md:text-base', // age/location captions — Space Mono 400
    profileSocialStat: 'font-syne !font-bold text-[40px] leading-[102%]',      // follower count — Syne 700
    profileStatCaption: 'font-spacemono !font-normal text-base leading-[160%]', // follower label — Space Mono 400
    socialStat: 'font-syne !font-bold',             // large stats/percentages — Syne 700
    label: 'font-spacemono !font-normal',           // labels — Space Mono 400
    body: 'font-worksans !font-normal',             // ordinary values/text — Work Sans 400
    button: 'font-worksans !font-semibold',         // buttons/CTAs — Work Sans 600
    verified: 'font-spacemono !font-normal',        // "Verified" badge — Space Mono 400
    favFactValue: 'font-syne !font-medium text-5xl sm:text-4xl lg:text-6xl leading-[100%]',
    favFactCaption: 'font-spacemono !font-medium uppercase tracking-[3px] text-sm',
    topInterestHeading: 'font-syne !font-semibold',
    topInterestItem: 'font-syne !font-bold uppercase text-[28px] md:text-[40px] lg:text-[56px] leading-[130%]',
    brandPartnersHeading: 'font-syne !font-semibold',
    brandPartnerName: 'font-syne !font-bold uppercase text-3xl',
    deliverablesHeading: 'font-syne !font-semibold',
    deliverableTitle: 'font-syne !font-bold text-[32px] md:text-4xl lg:text-5xl leading-[102%]',
    deliverableDescription: 'font-worksans !font-normal text-sm base:text-base leading-[150%]',
    ctaBannerTitle: 'font-syne !font-bold text-xl md:text-4xl lg:text-5xl',
    ctaBannerButton: 'font-spacemono !font-normal uppercase tracking-[0.1em] text-base',
    audienceHeading: 'font-syne !font-semibold',
    audienceSubHeading: 'font-worksans !font-medium text-lg base:text-2xl',
    audienceItemLabel: 'font-worksans !font-normal text-base',
    audienceItemValue: 'font-syne !font-bold text-base',
    ratesHeading: 'font-syne !font-semibold',
    rateChannelTitle: 'text-2xl smd:text-[28px] lg:text-[32px] xl:text-[40px] font-light md:font-medium font-worksans !font-medium leading-[130%]',
    ratePriceValue: 'text-2xl smd:text-[32px] font-worksans !font-medium font-light',
    topPostStatValue: 'font-worksans !font-medium text-xl smd:text-[22px] base:text-2xl lg:text-[28px] xl:text-[32px]',
    topPostStatCaption: 'font-spacemono !font-normal text-xs md:text-sm',
    topPostCaptionTitle: 'font-syne !font-bold text-base sm:text-2xl',
    topPostCaptionSubtitle: 'font-fraunces !font-normal italic text-sm sm:text-lg text-neutral',
    reviewCreatorName: 'font-syne !font-bold text-[40px] lg:text-6xl leading-[125%]',
    reviewOverallRating: 'font-syne !font-bold text-[40px] sm:text-5xl lg:text-6xl leading-[102%]',
    reviewOverallCaption: 'font-spacemono !font-normal uppercase tracking-[3px] text-sm md:text-lg',
    reviewCriteriaLabel: 'font-spacemono !font-normal uppercase tracking-[3px] text-base max-md:text-sm',
    reviewCardTitle: 'font-worksans !font-medium tracking-widest text-xl max-md:text-2xl md:uppercase',
    reviewCardBody: 'font-worksans !font-normal text-base leading-[130%]',
    topVideosBanner: 'font-worksans !font-normal text-base',
    reviewFeedbackSubline: 'font-worksans text-base',
    linksHeading: 'font-syne !font-semibold',
    linkItemTitle: 'font-worksans !font-medium text-2xl md:text-[34px] xl:text-[40px] leading-[130%]',
    socialsHeading: 'font-syne !font-semibold',
    socialHandle: 'font-worksans !font-normal text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    mailHeading: 'font-syne !font-semibold',
    mailAddress: 'font-worksans !font-normal text-lg md:text-xl lg:text-[22px] xl:text-2xl leading-[130%]',
    verificationBannerText: 'font-worksans !font-normal text-lg lg:text-xl',
    bio: 'Space Mono, monospace'
  }
};

export const FONT_GROUP_KEYS = ['standard', 'dark', 'natural', 'classic', 'combo'];

export const FONT_GROUP_LABELS = {
  standard: 'Pure',
  dark: 'Edge',
  natural: 'Noir',
  classic: 'Arc',
  combo: 'Combo'
};
